import { getModule, VuexModule, Mutation, Module, Action } from 'vuex-module-decorators';
import store from '@/store/index';
import NavigationMenuItem from '@/types/NavigationMenuItem';
import NavigationMenu from '@/types/NavigationMenu';
import Routes from '@/types/Routes';

@Module({ dynamic: true, namespaced: true, store, name: 'navigation' })
export class NavigationModule extends VuexModule {
  activeMenuItem = {
    activeRoute: '',
    NavigationMenuItem: {}
  };

  menu: NavigationMenu = {} as NavigationMenu;
  private initialMenu: NavigationMenu = null;

  @Action
  initMenuAction(params) {
    const { isAdmin, route } = params;

    this.initDefaultMenu();

    if (isAdmin) {
      this.addMailSenderToMenu();
      this.addReportsToMenu();
    }

    this.initMenu(route);
  }

  @Mutation
  initMenu(route: string | any) {
    if (this.activeMenuItem.activeRoute === route) {
      return;
    }

    // init default first menu item
    if (!route) {
      this.menu = JSON.parse(JSON.stringify(this.initialMenu));

      const firstParent = this.menu.items[0];
      firstParent.isActive = true;

      if (firstParent.items && firstParent.items.length) {
        const firstChild = firstParent.items[0];
        firstChild.isActive = true;

        this.activeMenuItem.activeRoute = `${firstParent.routeName}|${firstChild.routeName}`;
      } else {
        this.activeMenuItem.activeRoute = `${firstParent.routeName}`;
      }
      this.activeMenuItem.NavigationMenuItem = firstParent;
    }

    if (route === 'root') {
      this.menu = JSON.parse(JSON.stringify(this.initialMenu));

      const rootMenuItem = this.menu.items[0];
      rootMenuItem.isActive = true;

      this.activeMenuItem.NavigationMenuItem = rootMenuItem;
      this.activeMenuItem.activeRoute = route;

      return;
    }

    const pathTree = route.split('|');
    if (pathTree.length === 2 && pathTree[0] && pathTree[1]) {
      this.menu = JSON.parse(JSON.stringify(this.initialMenu));

      const menuItemRouteName = pathTree[0];
      const subMenuItemRouteName = pathTree[1];

      const menuItemIndex = this.menu.items.findIndex((_) => _.routeName === menuItemRouteName);
      if (menuItemIndex > -1) {
        const subMenuItemIndex = this.menu.items[menuItemIndex].items.findIndex((_) => _.routeName === subMenuItemRouteName);

        const menuItem = this.menu.items[menuItemIndex];
        const subMenuItem = menuItem.items[subMenuItemIndex];

        menuItem.isActive = true;
        subMenuItem.isActive = true;

        this.activeMenuItem.activeRoute = `${menuItem.routeName}|${subMenuItem.routeName}`;
        this.activeMenuItem.NavigationMenuItem = menuItem;
      } else {
        console.log('root index was not found. looking root: ' + menuItemRouteName);
      }
    } else {
      this.menu = JSON.parse(JSON.stringify(this.initialMenu));

      const mainMenuItem = this.menu.items.find((_) => _.routeName === route);

      if (!mainMenuItem) {
        this.menu.items.forEach((menu) => {
          menu.isActive = false;

          if (menu.items) {
            menu.items.forEach((subMenu) => {
              subMenu.isActive = false;
            });
          }
        });

        this.activeMenuItem.NavigationMenuItem = {};

        return;
      }

      mainMenuItem.isActive = true;

      let futureActiveRoute = '';

      if (mainMenuItem.items && mainMenuItem.items.length) {
        mainMenuItem.items[0].isActive = true;
        futureActiveRoute = `${mainMenuItem.routeName}|${mainMenuItem.items[0].routeName}`;
      } else {
        futureActiveRoute = mainMenuItem.routeName;
      }

      this.activeMenuItem.activeRoute = futureActiveRoute;
      this.activeMenuItem.NavigationMenuItem = mainMenuItem;
    }
  }

  @Mutation
  updateMenuItem(newActiveItem: NavigationMenuItem) {
    const store = getModule(NavigationModule);

    const route = newActiveItem.rootName ? `${newActiveItem.rootName}|${newActiveItem.routeName}` : newActiveItem.routeName;

    store.initMenu(route);
  }

  @Mutation
  addMailSenderToMenu() {
    if (this.initialMenu.items.some((m) => m.routeName === Routes.mailSender.name)) {
      return;
    }

    const mailSender = {
      isActive: false,
      rootName: '',
      routeName: Routes.mailSender.name,
      title: Routes.mailSender.title,
      items: []
    } as NavigationMenuItem;

    this.initialMenu.items.push(mailSender);
  }

  @Mutation
  addReportsToMenu() {
    if (this.initialMenu.items.some((m) => m.routeName === Routes.reports.name)) {
      return;
    }

    const reports = {
      isActive: false,
      rootName: '',
      routeName: Routes.reports.name,
      title: Routes.reports.title,
      items: [
        {
          isActive: false,
          rootName: Routes.reports.name,
          routeName: Routes.reports.accountsLog.name,
          title: Routes.reports.accountsLog.title,
          items: []
        },
        {
          isActive: false,
          rootName: Routes.reports.name,
          routeName: Routes.reports.licenseLog.name,
          title: Routes.reports.licenseLog.title,
          items: []
        },
        {
          isActive: false,
          rootName: Routes.reports.name,
          routeName: Routes.reports.activationLog.name,
          title: Routes.reports.activationLog.title,
          items: []
        },
        {
          isActive: false,
          rootName: Routes.reports.name,
          routeName: Routes.reports.errorsLog.name,
          title: Routes.reports.errorsLog.title,
          items: []
        },
        {
          isActive: false,
          rootName: Routes.reports.name,
          routeName: Routes.reports.mailLog.name,
          title: Routes.reports.mailLog.title,
          items: []
        }
      ]
    } as NavigationMenuItem;

    this.initialMenu.items.push(reports);
  }

  @Action
  resetMenuAction() {
    this.resetMenu();

    this.initDefaultMenu();
  }

  @Mutation
  resetMenu() {
    this.menu = { items: [] };
    this.activeMenuItem = { activeRoute: '', NavigationMenuItem: { items: [] } };
  }

  @Mutation
  initDefaultMenu() {
    this.initialMenu = {
      items: [
        {
          isActive: false,
          rootName: '',
          routeName: Routes.root.name,
          title: Routes.dashboard.title,
          items: []
        },
        {
          isActive: false,
          rootName: '',
          routeName: Routes.management.name,
          title: Routes.management.title,
          items: [
            {
              isActive: false,
              rootName: Routes.management.name,
              routeName: Routes.management.accounts.name,
              title: Routes.management.accounts.title,
              items: []
            },
            {
              isActive: false,
              rootName: Routes.management.name,
              routeName: Routes.management.licenses.name,
              title: Routes.management.licenses.title,
              items: []
            },
            {
              isActive: false,
              rootName: Routes.management.name,
              routeName: Routes.management.activations.name,
              title: Routes.management.activations.title,
              items: []
            }
          ]
        }
      ]
    };
  }
}

export const NavigationStore = getModule(NavigationModule);
